'use client';

import { useEffect, useRef } from 'react';
import { trackFathomEvent } from '@/lib/fathom/trackFathomEvent';
import type { AllFathomEventNames } from '@/lib/fathom/fathom-events';
import type { FormErrorStatus } from '@/lib/forms/form-error-messages';
import {
  WORK_EMAIL_REASON_PARAM,
  WORK_EMAIL_REJECT_REASONS,
  type WorkEmailRejectReason,
} from '@/lib/forms/is-work-email';

/**
 * Work-email gate monitoring. The gate stays; these events only make its block rate
 * visible in Fathom (it was invisible: the client block returns before the attempt
 * event, and the server rejection only reached Lightsail logs).
 *
 * Fathom events carry no properties (only `_value`), so the blocked domain can't ride
 * the event. Instead the client block is one event (the curated client list is
 * free-mail by construction) and the server rejection is split by the server's
 * `reason` — a bounded set, never per-domain event names. Domains land in the server log.
 */

/** A strict form's co-located work-email events (see `FATHOM_EVENTS.<form>.hero.work_email`). */
export interface WorkEmailEvents {
  blocked: AllFathomEventNames;
  warning_shown: AllFathomEventNames;
  submitted_after_warning: AllFathomEventNames;
  /** The free Build Scan link shown after a blocked submit was clicked. */
  build_scan_click: AllFathomEventNames;
  rejected: Record<WorkEmailRejectReason, AllFathomEventNames>;
}

const WORK_EMAIL_ERROR: FormErrorStatus = 'work-email-required';

/** Narrow a raw `?error_reason=` value to a known rejection reason, else null (a repeated
 * param arrives as an array and is rejected, like `resolveFormErrorMessage`). */
export function parseWorkEmailRejectReason(value: string | string[] | undefined): WorkEmailRejectReason | null {
  return typeof value === 'string' && (WORK_EMAIL_REJECT_REASONS as readonly string[]).includes(value)
    ? (value as WorkEmailRejectReason)
    : null;
}

/** The rejected event for a rendered error card, or null when the card isn't a
 * work-email rejection or the reason is missing/unknown (e.g. a hand-crafted URL). */
export function workEmailRejectedEvent(
  errorStatus: string | string[] | undefined,
  reason: string | string[] | undefined,
  events: WorkEmailEvents,
): AllFathomEventNames | null {
  if (errorStatus !== WORK_EMAIL_ERROR) return null;
  const parsed = parseWorkEmailRejectReason(reason);
  return parsed ? events.rejected[parsed] : null;
}

/** Fire the server-rejection event once when the work-email error card renders.
 * `isShown` must mirror the component's own error-card condition. After firing, the
 * reason param is dropped from the URL (the card is keyed on `error`, so it still
 * renders) — a reload or back/forward to this entry then carries no reason and can't
 * re-count the rejection. */
export function useWorkEmailRejectedEvent(
  isShown: boolean,
  errorStatus: string | string[] | undefined,
  reason: string | string[] | undefined,
  events: WorkEmailEvents,
): void {
  const firedRef = useRef(false);
  const event = isShown ? workEmailRejectedEvent(errorStatus, reason, events) : null;
  useEffect(() => {
    if (!event || firedRef.current) return;
    firedRef.current = true;
    trackFathomEvent(event);
    const url = new URL(window.location.href);
    url.searchParams.delete(WORK_EMAIL_REASON_PARAM);
    window.history.replaceState(null, '', url);
  }, [event]);
}
